// The lot-state parts of the change-queue replay (BL-2681): the pass's copy of the lots' desired states,
// the store the lot-state sender writes through, and the actions a lot-state row offers. Split from
// change-queue-replay.ts to keep that file under its size limit.
import { lotGroupKey, readQueuedEntry } from '@auctogon/admin-isomorphic/isomorphic/offline/change-queue-entry';
import { markOverwrite, markRetry, type LotDesiredState } from '@auctogon/admin-isomorphic/isomorphic/offline/lot-desired-state';
import type { OfflineMediaStaging, OfflineStore, OutboxEntry } from '../../gateways/native-device';
import { requestFullDownload } from '../offline-sync/apply-auction-delta';
import { allStagedFiles, mediaOf } from '@auctogon/admin-isomorphic/isomorphic/offline/lot-state-media';
import { stagedFilesOf } from './change-queue-uploads';
import type { ChangeQueueActionResult } from './change-queue-replay';

/** What the pass-copy helpers need of the replay's pass context: the store and the pass's current copy. */
export interface LotStateViewHost {
  deps: { store: OfflineStore };
  view: { lotStates: LotDesiredState[] | null; version: number } | null;
}

/** The lots' desired states: the pass's copy while it is current, else one listing (kept only by the pass). */
export async function readLotStates(host: LotStateViewHost, opts: { keep: boolean }): Promise<LotDesiredState[]> {
  const view = host.view;
  if (view?.lotStates != null) return view.lotStates;
  const version = view?.version;
  const states = await host.deps.store.lotStates.list();
  if (opts.keep && view != null && host.view === view && view.version === version) view.lotStates = states;
  return states;
}

/** Keep the pass's copy of the states in step with a write the sender just made (`null`: removed). */
export function patchLotState(host: LotStateViewHost, input: { lotId: string; state: LotDesiredState | null }): void {
  const states = host.view?.lotStates;
  if (states == null) return;
  const at = states.findIndex((s) => s.lotId === input.lotId);
  if (input.state == null) {
    if (at !== -1) states.splice(at, 1);
  } else if (at === -1) states.push(input.state);
  else states[at] = input.state;
}

/** The store the pass's sender writes through: its state listings and writes keep the pass's copy current. */
export function senderStore(host: LotStateViewHost, readOutbox: () => Promise<OutboxEntry[]>): OfflineStore {
  const { store } = host.deps;
  return {
    ...store,
    // A copy: the pass patches its own array in place as it writes.
    outbox: { ...store.outbox, list: async () => [...(await readOutbox())] },
    lotStates: {
      ...store.lotStates,
      list: () => readLotStates(host, { keep: true }),
      update: async (lotId, change) => {
        const next = await store.lotStates.update(lotId, change);
        patchLotState(host, { lotId, state: next });
        return next;
      },
      remove: async (lotId) => {
        const removed = await store.lotStates.remove(lotId);
        patchLotState(host, { lotId, state: null });
        return removed;
      },
    },
  };
}

/** Delete the staged photos and videos a lot's desired state still names (BL-2815): a discarded lot must leave none behind. */
async function dropStateFiles(store: OfflineStore, media: OfflineMediaStaging | null, lotId: string): Promise<void> {
  const state = await store.lotStates.get(lotId);
  if (state == null) return;
  for (const fileId of allStagedFiles(mediaOf(state))) await media?.delete(fileId);
}

/** Remove a never-created lot's desired state and the photos and documents waiting on it. */
export async function dropCopyLot(store: OfflineStore, media: OfflineMediaStaging | null, lotId: string): Promise<void> {
  await dropStateFiles(store, media, lotId);
  await store.lotStates.remove(lotId);
  const group = lotGroupKey(lotId);
  for (const e of await store.outbox.list()) {
    if (e.groupKey !== group) continue;
    const read = readQueuedEntry(e);
    if (read.ok) for (const fileId of stagedFilesOf(read.payload)) await media?.delete(fileId);
    await store.outbox.remove(e.id);
  }
}

/** Move a lot's desired state from a relist copy's client id to the copy's server id, in one batch. */
export async function rekeyLotState(store: OfflineStore, ids: { clientId: string; serverId: string }): Promise<void> {
  const state = await store.lotStates.get(ids.clientId);
  if (state == null) return;
  const result = await store.outbox.appendLocalWrite({
    entries: [],
    lots: { put: [], delete: [] },
    lotStates: { put: [{ ...state, lotId: ids.serverId, send: null }], delete: [ids.clientId] },
  });
  if (!result.success) throw new Error('change queue: a lot state could not follow its relist copy');
}

/** Retry on a lot's failed desired state; undefined when `id` names no lot state. */
export async function retryLotState(store: OfflineStore, id: string): Promise<ChangeQueueActionResult | undefined> {
  // A relist's copy shares its id with the queued relist, and the relist is the one the operator acts on.
  if ((await store.outbox.get(id)) != null) return undefined;
  const state = await store.lotStates.get(id);
  if (state == null) return undefined;
  if (state.status !== 'failed') return { success: false, code: 'NOT_FAILED' };
  await store.lotStates.update(id, (cur) => (cur?.status === 'failed' ? markRetry(cur) : cur));
  return { success: true };
}

/** Overwrite on a lot's refused desired state; undefined when `id` names no lot state. */
export async function overwriteLotState(store: OfflineStore, id: string): Promise<ChangeQueueActionResult | undefined> {
  // A relist's copy shares its id with the queued relist, and the relist is the one the operator acts on.
  if ((await store.outbox.get(id)) != null) return undefined;
  const state = await store.lotStates.get(id);
  if (state == null) return undefined;
  if (state.status !== 'refused') return { success: false, code: 'NOT_REFUSED' };
  await store.lotStates.update(id, (cur) => (cur?.status === 'refused' ? markOverwrite(cur) : cur));
  return { success: true };
}

/**
 * Keep (Discard) on a lot's failed or refused desired state: the phone's change is dropped. A lot
 * made offline is deleted from the device along with what waits on it; any other lot's auction is
 * downloaded again so the device shows the server's value. Undefined when `id` names no lot state.
 */
export async function discardLotState(deps: { store: OfflineStore; media: OfflineMediaStaging | null }, id: string): Promise<ChangeQueueActionResult | undefined> {
  const { store, media } = deps;
  // A relist's copy shares its id with the queued relist, and the relist is the one the operator acts on.
  if ((await store.outbox.get(id)) != null) return undefined;
  const state = await store.lotStates.get(id);
  if (state == null) return undefined;
  if (state.status !== 'failed' && state.status !== 'refused') return { success: false, code: 'NOT_FAILED' };
  for (const fileId of allStagedFiles(mediaOf(state))) await media?.delete(fileId);
  await store.lotStates.remove(id);
  if (state.kind === 'create') {
    // The lot never reached the server: its photos and documents waiting on it can never be added.
    await dropCopyLot(store, media, id);
    await store.records.delete({ collection: 'lots', key: id });
    return { success: true };
  }
  const lot = await store.records.get({ collection: 'lots', key: id });
  if (lot?.detail.auctionId) await requestFullDownload({ store, auctionId: lot.detail.auctionId });
  return { success: true };
}
